import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import LogoutButton from '../auth/LogoutButton';
import './layout.css';

/**
 * BottomTabBar — mobile navigation (hidden on desktop via CSS).
 *
 * Shows 4 pinned tabs + a "More" button that opens a full-screen drawer
 * listing all remaining sections. This avoids a horizontally scrolling bar.
 *
 * Props:
 *   sections  - array of { id, label, icon, path, visible } objects
 */

// These 4 IDs are always pinned as dedicated tabs.
const PINNED_IDS = ['overview', 'bottleFeeds', 'diapers', 'sleepTracking'];

export default function BottomTabBar({ sections = [] }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const visibleSections = sections.filter((s) => s.visible !== false);
  const pinned = PINNED_IDS
    .map((id) => visibleSections.find((s) => s.id === id))
    .filter(Boolean);
  const overflow = visibleSections.filter((s) => !PINNED_IDS.includes(s.id));

  const handleOverflowNav = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <>
      <nav
        className="bottom-tab-bar"
        aria-label="Mobile navigation"
        data-testid="bottom-tab-bar"
        data-tutorial="bottom-tab-bar"
      >
        {pinned.map((section) => (
          <NavLink
            key={section.id}
            to={section.path}
            end={section.path === '/app'}
            className={({ isActive }) =>
              `bottom-tab-item${isActive ? ' active' : ''}`
            }
            {...(section.id === 'bottleFeeds' ? { 'data-tutorial': 'bottom-tab-bottle' } : {})}
          >
            {section.icon && (
              <span className="bottom-tab-icon" aria-hidden="true">
                {section.icon}
              </span>
            )}
            <span className="bottom-tab-label">{section.label}</span>
          </NavLink>
        ))}

        {/* More button — only shown if there are overflow sections */}
        {overflow.length > 0 && (
          <button
            className={`bottom-tab-item${drawerOpen ? ' active' : ''}`}
            onClick={() => setDrawerOpen((o) => !o)}
            aria-label="More sections"
            aria-expanded={drawerOpen}
          >
            <span className="bottom-tab-icon" aria-hidden="true">☰</span>
            <span className="bottom-tab-label">More</span>
          </button>
        )}
      </nav>

      {/* More drawer */}
      {drawerOpen && (
        <>
          <div
            className="bottom-drawer-backdrop"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="bottom-drawer" role="dialog" aria-label="More sections">
            <div className="bottom-drawer-header">
              <span className="bottom-drawer-title">All sections</span>
              <button
                className="bottom-drawer-close"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <ul className="bottom-drawer-list">
              {overflow.map((section) => (
                <li key={section.id}>
                  <button
                    className="bottom-drawer-item"
                    onClick={() => handleOverflowNav(section.path)}
                  >
                    <span className="bottom-drawer-icon" aria-hidden="true">
                      {section.icon}
                    </span>
                    {section.label}
                  </button>
                </li>
              ))}
              <li className="bottom-drawer-logout">
                <LogoutButton />
              </li>
            </ul>
          </div>
        </>
      )}
    </>
  );
}
